import React, { useEffect, useRef, useState } from "react";
import FrontendLayout from "../../layouts/FrontendLayout";
import { Link, useNavigate } from "react-router-dom";
import { API_BASE_URL, fetchData } from "../../api/api";
import FAQ from "../../components/frontend/home/FAQ";
import Spinner from "../../components/common/Spinner";
import Select from "react-select";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import { BASEURL } from "../../constant/common";

const HomePage = () => {
  const navigate = useNavigate();

  const didFetch = useRef(false);

  const [isLoading, setIsLoading] = useState(false);

  const [servicesList, setServicesList] = useState([]);
  const [selectedServices, setSelectedServices] = useState({
    value: null,
    label: null,
    slug: null,
  });

  const [includeSurrounding, setIncludeSurrounding] = useState(false);

  const [suburbList, setSuburbList] = useState([]);
  const [selectedSuburb, setSelectedSuburb] = useState([]);
  // const [selectedSuburb, setSelectedSuburb] = useState(() => {
  //   const saved = sessionStorage.getItem("selectedSuburb");
  //   return saved ? JSON.parse(saved) : [];
  // });
  const [filteredSuburbs, setFilteredSuburbs] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [typingTimeout, setTypingTimeout] = useState(null);

  const handleInputChange = (newValue) => {
    setInputValue(newValue);

    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    const timeout = setTimeout(() => {
      filterSuburbs(newValue);
    }, 300);

    setTypingTimeout(timeout);
  };

  const filterSuburbs = (searchText) => {
    if (searchText.length >= 2) {
      const filtered = suburbList
        .filter((item) => {
          const combined = `${item.name} (${item.postcode})`.toLowerCase();
          return combined.includes(searchText.toLowerCase());
        })
        .slice(0, 50);

      setFilteredSuburbs(filtered);
    } else {
      setFilteredSuburbs([]);
    }
  };

  const [isViewAll, setIsViewAll] = useState(false);

  const getServices = async () => {
    setIsLoading(true);
    try {
      const data = await fetchData("/get-services", { status: 1 });
      setServicesList(data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getSuburb = async (fk_postcodes_id = null) => {
    try {
      const data = await fetchData("/get-suburbs", {
        status: 1,
        fk_postcodes_id: fk_postcodes_id,
      });
      setSuburbList(data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
    }
  };

  const toggleView = () => {
    setIsViewAll(!isViewAll);
  };

  useEffect(() => {
    if (!didFetch.current) {
      getServices();
      getSuburb();
      didFetch.current = true;
    }
  }, []);

  const handleOnClickSearch = (e) => {
    e.preventDefault();

    if (!selectedServices?.value) {
      toast.error("Please select a practitioner.");
      return;
    }

    if (!selectedSuburb?.length > 0) {
      toast.error("Please enter post code.");
      return;
    }

    if (selectedSuburb?.length === 1) {
      const navigateURL =
        "/" +
        selectedSuburb[0]?.postcode +
        "/" +
        selectedServices?.slug +
        "-in-" +
        selectedSuburb[0]?.slug;
      navigate(navigateURL);
    } else {
      const navigateURL = "search/" + selectedServices?.slug;
      sessionStorage.setItem("selectedSuburb", JSON.stringify(selectedSuburb));
      sessionStorage.setItem("includeSurrounding", includeSurrounding);
      navigate(navigateURL);

    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "AlliedHealth.com.au",
    url: "https://www.alliedhealth.com.au",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://www.alliedhealth.com.au/search?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
    publisher: {
      "@type": "Organization",
      name: "AlliedHealth.com.au",
      url: "https://www.alliedhealth.com.au",
      logo: {
        "@type": "ImageObject",
        url: "https://www.alliedhealth.com.au/assets/image/logo.png",
      },
    },
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      <Helmet>
        <title>
          AlliedHealth.com.au | Find, Compare & Review Allied Health
          Practitioners
        </title>

        <meta charset="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="theme-color" content="#000000" />

        {/* <!-- SEO Meta Tags --> */}
        <meta
          name="description"
          content="Find, compare, and review top allied health practitioners across Australia. Explore services, read patient reviews, and make informed healthcare choices with AlliedHealth.com.au."
        />
        <meta
          name="keywords"
          content="allied health, allied health practitioners, compare health services, health practitioner reviews, physiotherapy, occupational therapy, speech therapy, healthcare Australia"
        />
        <meta name="robots" content="index, follow" />

        <link rel="canonical" href={BASEURL} />
        {/* <!-- Open Graph / Social Media --> */}

        <meta
          property="og:title"
          content="AlliedHealth.com.au | Find, Compare & Review Allied Health Practitioners"
        />

        <meta
          property="og:description"
          content="Australia's go-to platform to search, compare and review allied health professionals. Discover the best care near you."
        />

        <meta property="og:url" content="https://www.alliedhealth.com.au" />

        <meta property="og:type" content="website" />

        <meta
          property="og:image"
          content="https://www.alliedhealth.com.au/assets/image/logo.png"
        />

        {/* <!-- Twitter Card --> */}

        <meta name="twitter:card" content="summary_large_image" />

        <meta
          name="twitter:title"
          content="AlliedHealth.com.au | Find, Compare & Review Allied Health Practitioners"
        />

        <meta
          name="twitter:description"
          content="Search, compare, and review allied health providers in Australia. Make informed decisions about your healthcare."
        />
        <meta
          name="twitter:image"
          content="https://www.alliedhealth.com.au/assets/image/logo.png"
        />

        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <FrontendLayout>
        <section>
          <div className="container px-3 mx-auto flex flex-col items-center justify-center pt-[40px]  md:pt-[80px] pb-[60px] md:pb-[100px]">
            <h1 className="text-[28px] leading-relaxed sm:text-[39px] sm:leading-[59px] text-dark font-bold text-center mb-2.5">
              Compare Allied Health Practitioners
            </h1>
            <p className="text-dark text-lg text-center mb-6 md:mb-[50px]">
              Easily compare practitioners based on services, location, client
              ratings and more!
            </p>
            <form className="flex flex-wrap max-w-[1072px] w-full mx-auto mb-10 md:mb-20">
              <div className="basis-full sm:basis-3/6 md:basis-2/5 p-2.5">
                <div className="relative">
                  <div className="pl-[75px] text-dark text-lg bg-white rounded-xl w-full outline-none">
                    <Select
                      required
                      options={servicesList.map((item) => ({
                        value: item.services_id,
                        label: item.name,
                        slug: item.slug,
                      }))}
                      className="w-full text-black custom-select no-border no-icon"
                      placeholder="Select Practitioner"
                      onChange={(selectedOption) => {
                        setSelectedServices(selectedOption);
                      }}
                      value={selectedServices?.value ? selectedServices : null}
                    />
                  </div>
                  <img
                    src="/assets/image/svg/compare-input-a.svg"
                    alt=""
                    srcSet=""
                    className="absolute top-1/2 left-6 transform -translate-y-1/2"
                  />
                </div>
              </div>
              <div className="basis-full sm:basis-3/6 md:basis-2/5 p-2.5">
                <div className="relative">
                  <div className="pl-[75px] text-dark text-lg bg-white rounded-xl w-full outline-none">
                    <Select
                      required
                      isMulti
                      options={filteredSuburbs.map((item) => ({
                        value: item.suburbs_id,
                        label: `${item.name} (${item.postcode})`,
                        name: item.name,
                        slug: item.slug,
                        fk_postcodes_id: item.fk_postcodes_id,
                        postcode: item.postcode,
                      }))}
                      className="w-full text-black custom-select no-border no-icon"
                      placeholder="Enter Postcode"
                      onChange={(selectedOptions) => {
                        setSelectedSuburb(selectedOptions || []);
                      }}
                      value={selectedSuburb}
                      onInputChange={handleInputChange}
                      isClearable
                      isSearchable
                      noOptionsMessage={() =>
                        inputValue.length <= 2
                          ? "Enter Postcode"
                          : "No options available"
                      }
                      menuIsOpen={inputValue.length > 2}
                    />
                  </div>
                  <img
                    src="/assets/image/svg/compare-input-b.svg"
                    alt=""
                    srcSet=""
                    className="absolute top-1/2 left-6 transform -translate-y-1/2"
                  />
                </div>
              </div>

              <div className="basis-full md:basis-1/5 p-2.5">
                <button
                  onClick={handleOnClickSearch}
                  type="submit"
                  className="block px-3 py-[18px] h-[60px] text-white transition duration-100 ease-in-out bg-blue-500  rounded-md shadow-sm  bg-green text-base font-semibold w-full text-center"
                >
                  Search
                </button>
              </div>

              {/* Checkbox - only visible if multiple suburbs */}
              {/* {selectedSuburb.length > 1 && (
                <div className="basis-full p-2.5">
                  <div className="text-dark text-lg bg-white rounded-xl w-full outline-none flex px-4 py-3">
                    <input
                      type="checkbox"
                      id="includeSurrounding"
                      checked={includeSurrounding}
                      onChange={(e) => setIncludeSurrounding(e.target.checked)}
                      className="w-5 h-5 text-blue-500 border-gray-300 rounded focus:ring-blue-400 focus:outline-none cursor-pointer"
                    />
                    <label
                      htmlFor="includeSurrounding"
                      className="ml-3 text-base text-gray-700 select-none cursor-pointer"
                    >
                      Include surrounding areas
                    </label>
                  </div>
                </div>
              )} */}
            </form>

            <div
              id="gridContainer"
              className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-[15px] sm:gap-x-[24px] lg:gap-x-[30px] gap-y-[15px] sm:gap-y-[24px] w-full"
            >
              {servicesList?.length > 0 &&
                servicesList
                  .slice(0, isViewAll ? servicesList?.length : 8)
                  .map((service, index) => (
                    <h2
                      onClick={() => {
                        setSelectedServices({
                          value: service?.services_id,
                          label: service?.name,
                          slug: service?.slug,
                        });
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      key={index}
                      className="cursor-pointer p-3 sm:p-5 bg-white rounded-xl flex flex-col items-center justify-center gap-y-3 hover:shadow-card service-box"
                    >
                      <img
                        src={`${
                          service?.image != null
                            ? API_BASE_URL + service?.image
                            : ""
                        }`}
                        alt={service?.name}
                      />
                      <span className="text-base lg:text-xl sm:leading-9 text-dark font-medium text-center">
                        {service?.name}
                      </span>
                    </h2>
                  ))}
              {servicesList?.length > 8 && (
                <div className="col-span-2 md:col-span-3 xl:col-span-4">
                  <h2
                    type="button"
                    id="toggleButton"
                    onClick={toggleView}
                    className="cursor-pointer px-11 py-2 bg-blue text-white rounded-[8px] font-bold font-poppins text-lg border-2 border-solid border-blue hover:scale-[1.04] transition ease-in-out flex max-w-[240px] w-full justify-center mx-auto items-center gap-2 mt-5"
                  >
                    {isViewAll ? "View Less" : "View All"}
                    <svg
                      width="14"
                      height="8"
                      viewBox="0 0 14 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className={`transition-transform duration-300 ${
                        isViewAll ? "rotate-180" : "rotate-0"
                      }`}
                    >
                      <path
                        d="M14 1C14.0008 1.13161 13.9756 1.26207 13.9258 1.38391C13.876 1.50574 13.8027 1.61656 13.71 1.71L7.71003 7.71C7.52267 7.89625 7.26921 8.00079 7.00503 8.00079C6.74084 8.00079 6.48739 7.89625 6.30003 7.71L0.300027 1.71C0.136201 1.5187 0.0505946 1.27262 0.0603158 1.02095C0.0700369 0.769271 0.174369 0.530532 0.352463 0.352437C0.530558 0.174342 0.769297 0.0700096 1.02097 0.0602884C1.27265 0.0505672 1.51873 0.136174 1.71003 0.3L7.00003 5.59L12.29 0.3C12.4293 0.159578 12.6071 0.063607 12.8009 0.0242218C12.9947 -0.0151634 13.1958 0.00380633 13.3789 0.0787319C13.5619 0.153658 13.7186 0.281174 13.8292 0.445157C13.9397 0.609141 13.9992 0.802227 14 1Z"
                        fill="white"
                      />
                    </svg>
                  </h2>
                </div>
              )}
            </div>
          </div>
        </section>
        <section className="bg-white">
          <div className="container mx-auto px-3 pt-[40px] md:pt-[80px] pb-[60px] md:pb-[110px]">
            <div className="flex flex-wrap mb-[40px] md:mb-[80px] justify-center">
              <div className="p-2 basis-[100%] sm:basis-1/2 lg:basis-1/3">
                <div className="border border-solid border-green rounded-xl py-6 px-4 flex items-center justify-center gap-4">
                  <img src="/assets/image/svg/obligationfree.svg" alt="" />
                  <span className="text-xl text-dark font-semibold text-center">
                    No Obligation
                  </span>
                </div>
              </div>
              <div className="p-2 basis-[100%] sm:basis-1/2 lg:basis-1/3">
                <div className="border border-solid border-green rounded-xl py-6 px-4 flex items-center justify-center gap-4">
                  <img src="/assets/image/svg/nofee.svg" alt="" />
                  <span className="text-xl text-dark font-semibold text-center">
                    100% Free for Families
                  </span>
                </div>
              </div>
              <div className="p-2 basis-[100%] sm:basis-1/2 lg:basis-1/3">
                <div className="border border-solid border-green rounded-xl py-6 px-4 flex items-center justify-center gap-4">
                  <img src="/assets/image/svg/savetime.svg" alt="" />
                  <span className="text-xl text-dark font-semibold text-center">
                    Save Hours of Time
                  </span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-12 gap-[24px] lg:gap-[30px]">
              <div className="col-span-12 md:col-span-5">
                <h2 className="text-[24px] sm:text-[34px] leading-normal text-blue font-bold mb-[16px] sm:mb-[24px] lg:mb-[30px]">
                  <span className="font-normal text-dark block">
                    Looking for a
                  </span>
                  Allied Health Practitioners?
                </h2>
                <button
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="px-6 py-2 bg-green text-white rounded-[8px] font-bold font-poppins text-lg border-2 border-solid border-green hover:scale-[1.04] transition ease-in-out  max-w-[240px] w-full mx-auto mt-5 block ml-0 text-center"
                >
                  Let’s connect you
                </button>
              </div>
              <div className="col-span-12 md:col-span-7">
                <p className="text-base text-dark mb-3">
                  Connecting you with Trusted Allied Health Practitioners: We
                  understand the importance of holistic care, which is why we
                  connect you with a wide range of experienced allied health
                  practitioners. Whether you're seeking physiotherapy, speech
                  therapy, occupational therapy, dietetics, or other health
                  services, we are here to help you find the right professionals
                  to meet your needs.{" "}
                </p>
                <p className="text-base text-dark">
                  We’re here to support you every step of the way to find
                  Trusted Allied Health Practitioners. Click on Let’s connect
                  you button to get started and we will connect you with an
                  allied health practitioner today!{" "}
                </p>
              </div>
            </div>
          </div>
        </section>
        <section className="py-[40px] md:py-[80px]" id="Howitworks">
          <div className="container mx-auto px-3">
            <h2 className="text-center text-[#222] text-[24px] sm:text-[34px] leading-relaxed font-semibold mb-[24px] sm:mb-[40px] lg:mb-[60px]">
              How it Works : 3 Easy Steps (Compare)
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[24px] lg:gap-[30px]">
              <div className="flex flex-col items-center justify-start px-3">
                <div className="max-w-[240px] rounded-lg border-[3px] border-green text-green w-full py-4 text-center font-bold text-xl sm:text-3xl mb-8">
                  STEP 1
                </div>
                <p className="text-black font-semibold text-lg mb-2 text-center max-w-[341px] w-100">
                  Select Your Practitioner & Enter Postcode
                </p>
                <p className="text-black font-normal text-base mb-2 text-center max-w-[341px] w-100">
                  Start by choosing a practitioner from our list of Allied
                  Health Practitioners. Enter your postcode to see practitioners
                  list in your area.
                </p>
              </div>
              <div className="flex flex-col items-center justify-start px-3">
                <div className="max-w-[240px] rounded-lg border-[3px] border-green text-green w-full py-4 text-center font-bold text-xl sm:text-3xl mb-8">
                  STEP 2
                </div>
                <p className="text-black font-semibold text-lg mb-2 text-center max-w-[341px] w-100">
                  View Practices & Practitioner Listings
                </p>
                <p className="text-black font-normal text-base mb-2 text-center max-w-[341px] w-100">
                  Once your practitioner and postcode are entered, you’ll be
                  presented with a list of practitioners & Practices. Filter
                  your search by Gender, Language to find the best match for
                  your needs.
                </p>
              </div>
              <div className="flex flex-col items-center justify-start px-3">
                <div className="max-w-[240px] rounded-lg border-[3px] border-green text-green w-full py-4 text-center font-bold text-xl sm:text-3xl mb-8">
                  STEP 3
                </div>
                <p className="text-black font-semibold text-lg mb-2 text-center max-w-[341px] w-100">
                  Compare Practices or Practitioners
                </p>
                <p className="text-black font-normal text-base mb-2 text-center max-w-[341px] w-100">
                  Compare the selected Practices or practitioners side-by-side
                  to make an informed decision. See detailed profiles, services
                  and reviews to choose the right fit for you.
                </p>
              </div>
            </div>
          </div>
        </section>
        <section className="py-[40px] md:py-[80px]" id="HowitworksFind">
          <div className="container mx-auto px-3">
            <h2 className="text-center text-[#222] text-[24px] sm:text-[34px] leading-relaxed font-semibold mb-[24px] sm:mb-[40px] lg:mb-[60px]">
              How it Works : 3 Easy Steps (Find)
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[24px] lg:gap-[30px]">
              <div className="flex flex-col items-center justify-start px-3">
                <div className="max-w-[240px] rounded-lg border-[3px] border-green text-green w-full py-4 text-center font-bold text-xl sm:text-3xl mb-8">
                  STEP 1
                </div>
                <p className="text-black font-semibold text-lg mb-2 text-center max-w-[341px] w-100">
                  Tell us about you
                </p>
                <p className="text-black font-normal text-base mb-2 text-center max-w-[341px] w-100">
                  Answer few questions about your requirement & preferences to
                  find the best suitable Allied Health Practitioner.
                </p>
              </div>
              <div className="flex flex-col items-center justify-start px-3">
                <div className="max-w-[240px] rounded-lg border-[3px] border-green text-green w-full py-4 text-center font-bold text-xl sm:text-3xl mb-8">
                  STEP 2
                </div>
                <p className="text-black font-semibold text-lg mb-2 text-center max-w-[341px] w-100">
                  Find the Right Practitioner
                </p>
                <p className="text-black font-normal text-base mb-2 text-center max-w-[341px] w-100">
                  Based on your preferences, we’ll help you find the right
                  practitioner who fits your requirements and location.
                </p>
              </div>
              <div className="flex flex-col items-center justify-start px-3">
                <div className="max-w-[240px] rounded-lg border-[3px] border-green text-green w-full py-4 text-center font-bold text-xl sm:text-3xl mb-8">
                  STEP 3
                </div>
                <p className="text-black font-semibold text-lg mb-2 text-center max-w-[341px] w-100">
                  We will connect you
                </p>
                <p className="text-black font-normal text-base mb-2 text-center max-w-[341px] w-100">
                  We’ll connect you with your chosen Practitioner, so you can
                  start receiving Allied Health services with Trusted
                  Practitioner.
                </p>
              </div>
            </div>
          </div>
        </section>
        <section
          className="pt-[40px] md:pt-[70px] pb-[60px] md:pb-[100px]"
          id="faqs"
        >
          <div className="container px-3 mx-auto">
            <div
              className="flex flex-wrap justify-between mb-[20px] md:mb-[30px] lg:mb-[40px] gap-3 fade-up show"
              style={{ "--delay": "0s" }}
            >
              <div className="w-fit">
                <h3 className="text-dark text-[24px] sm:text-[34px]  leading-normal font-semibold">
                  Frequently Asked Questions
                </h3>
              </div>
              <div className="max-w-[630px] w-full">
                <p className="text-dark text-lg"></p>
              </div>
            </div>

            <FAQ />
          </div>
        </section>
      </FrontendLayout>
    </>
  );
};

export default HomePage;
